import { HttpError, HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';

/**
 * Socket message flags. These can be combined.
 */
export enum SocketMessageFlags {
    None = 0,
    Data = 1,
    Progress = 2,
    Completed = 4,
    Error = 8,
    ConnectionError = 16,
    Exception = 32
}

/**
 * Socket message.
 */
export interface SocketMessage<T> {
    type: SocketMessageFlags;
    connectionError?: HttpError;
    message: T;
}

/**
 * SignalR based socket class.
 */
export abstract class SocketSignalR<T> {
    private connection: HubConnection;
    private started = false;
    private lastError: HttpError;
    private processHandler = (message: string) => this.processMessage(message);

    /**
     * Gets signalR connection URL.
     */
    private get url(): string {
        return `${this.gatewayUrl}${this.connectionUrl}`;
    }

    /**
     * Instantiates a new instance of the SocketSignalR class.
     */
    constructor(private gatewayUrl: string, private connectionUrl: string) {
        gatewayUrl = gatewayUrl ? gatewayUrl : '';
    }

    /**
     * Subscribe to the proxy with method name and handler.
     *
     * @param name the name of subscription for a method.
     */
    public subscribe(name: string): void {
        this.init();
        if (this.lastError) {
            // send last error;
            this.clientHandler({ type: SocketMessageFlags.Error, connectionError: this.lastError, message: null });
        }

        this.connection.on(name, this.processHandler);
    }

    /**
     * Unsubscribe to the subscribed method call.
     *
     * @param name the name of subscription for a method.
     */
    public unsubscribe(name: string): void {
        this.connection.off(name, this.processHandler);
    }

    /**
     * Start request connection.
     */
    public start(): Promise<any> {
        return this.connection.start().then(() => this.started = true);
    }

    /**
     * Stop request connection.
     */
    public stop(): void {
        this.started = false;
        this.connection.stop();
    }

    /**
     * Invoke a method with parameters.
     *
     * @param name the method name to execute.
     * @param args the parameters to pass.
     * @return Promise the promise object.
     */
    public invoke(name: string, ...args: any[]): Promise<T> {
        if (!this.started) {
            throw new Error('request socket has not been started.');
        }

        if (this.connection.state === HubConnectionState.Disconnected) {
            return this.start().then(() => this.connection.invoke<T>(name, ...args));
        }

        try {
            return this.connection.invoke<T>(name, ...args);
        } catch (error) {
            this.errorMessage(error);
            return Promise.reject(error);
        }
    }

    /**
     * The client handler.
     */
    protected abstract clientHandler(messages: SocketMessage<T>): void;

    /**
     * Process the message.
     *
     * @param messages the messages.
     */
    protected abstract processMessage(message: string): void;

    /**
     * Initiate the connection to gateway.
     */
    private init(): void {
        this.connection = new HubConnectionBuilder().withUrl(this.url).build();
    }

    /**
     * Error message from signalr connection.
     *
     * @param error the error produced on the connection.
     */
    private errorMessage(error: HttpError): void {
        if (this.clientHandler) {
            this.clientHandler({ type: SocketMessageFlags.ConnectionError,  connectionError: error, message: null });
        } else {
            this.lastError = error;
        }
    }
}
